"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SignalrService = void 0;
const signalr_1 = require("@microsoft/signalr");
const messaging_common_1 = require("@guided-methods/messaging-common");
const defaultRetryDelay = 3000;
class SignalrService {
    get connected() {
        return this._connected;
    }
    constructor(logger, tokenService, notificationServiceUrl, init, handleSignalRDowntime) {
        this.logger = logger;
        this.tokenService = tokenService;
        this.notificationServiceUrl = notificationServiceUrl;
        this.init = init;
        this.handleSignalRDowntime = handleSignalRDowntime;
        this.handlers = [];
        this._connected = false;
    }
    async connect(retryDelays = [defaultRetryDelay, defaultRetryDelay * 2, defaultRetryDelay * 3]) {
        const transportType = {
            transport: signalr_1.HttpTransportType.LongPolling,
            accessTokenFactory: () => this.tokenService.getToken().then(t => t ?? ""),
        };
        this.hubConnection = new signalr_1.HubConnectionBuilder()
            .withUrl(`${this.notificationServiceUrl}hubs/notificationservice`, transportType)
            .configureLogging(signalr_1.LogLevel.None)
            .withAutomaticReconnect(retryDelays)
            .build();
        try {
            this.hubConnection.onreconnecting((error) => {
                this._connected = false;
                this.handleSignalRDowntime();
                this.logger.warn({
                    message: 'SignalR connection lost, reconnecting...',
                    mode: messaging_common_1.LogMode.Framework,
                    layer: 'SignalR',
                });
            });
            this.hubConnection.onreconnected((id) => {
                this._connected = true;
                this.logger.info({
                    message: 'SignalR connection reconnected',
                    mode: messaging_common_1.LogMode.Framework,
                    layer: 'SignalR',
                });
                Promise.resolve(this.init(true)).catch((e) => this.logger.error({
                    message: `SignalR re-initialization failed: ${e instanceof Error ? e.message : 'Unknown error'}`,
                    mode: messaging_common_1.LogMode.Framework,
                    layer: 'SignalR',
                }));
            });
            this.hubConnection.onclose((error) => {
                this.handleSignalRDowntime();
                this.logger.error({
                    message: 'SignalR connection lost',
                    mode: messaging_common_1.LogMode.Framework,
                    layer: 'SignalR',
                });
            });
            this.hubConnection.keepAliveIntervalInMilliseconds = 10000;
            this.hubConnection.serverTimeoutInMilliseconds = 20000;
            await this.hubConnection.start().then(() => (this._connected = true));
        }
        catch (e) {
            this.handleSignalRDowntime();
            this.logger.error({
                message: 'SignalR connection failed',
                mode: messaging_common_1.LogMode.Framework,
                layer: 'SignalR',
            });
        }
        return this.init(false);
    }
    stop() {
        this.logger.debug({
            message: 'SignalR connection close requested',
            mode: messaging_common_1.LogMode.Framework,
            layer: 'SignalR',
        });
        return this.hubConnection ? this.hubConnection.stop() : Promise.resolve();
    }
    subscribe(groupId, notificationName = '') {
        if (this?.hubConnection)
            return this.hubConnection.invoke('subscribe', groupId, notificationName, '');
        return Promise.resolve(undefined);
    }
    unsubscribe(groupId) {
        if (this?.hubConnection)
            return this.hubConnection.invoke('unsubscribe', groupId, '', '');
        return Promise.resolve(undefined);
    }
    onGuideMessage(handler) {
        if (!this.hubConnection) {
            return;
        }
        const wrapped = this.wrapGuideMessageHandler(handler);
        this.handlers.push(wrapped);
        this.hubConnection.on('message', wrapped);
        return wrapped;
    }
    onStreamIoValues(handler) {
        if (!this.hubConnection) {
            return;
        }
        const wrapped = this.wrapStreamIoValuesHandler(handler);
        this.handlers.push(wrapped);
        this.hubConnection.on('message', wrapped);
        return wrapped;
    }
    offGuideMessage(handler) {
        if (!this.hubConnection) {
            return;
        }
        const handlers = this.handlers.filter((h) => h.handler === handler || h === handler);
        this.handlers = this.handlers.filter((h) => h.handler !== handler && h !== handler);
        if (this?.hubConnection === undefined) {
            throw new Error("No SignalR Hub Connection to wrap");
        }
        handlers.forEach((h) => this.hubConnection?.off('message', h));
    }
    wrapGuideMessageHandler(handler) {
        const wrapper = (ev) => {
            let result;
            try {
                if (ev.eventType !== 'GuideMessage')
                    return;
                this.logger.debug({
                    message: `Received message from SignalR: ${JSON.stringify(ev)}`,
                    mode: messaging_common_1.LogMode.Framework,
                    layer: 'SignalR',
                    request: ev
                });
                result = (0, messaging_common_1.validate)(messaging_common_1.GuideMessage, ev.context);
            }
            catch (e) {
                this.logger.warn({
                    message: `Unknown message format from SignalR, skipping, ${e instanceof Error ? e.message : "Unknown error"}`,
                    mode: messaging_common_1.LogMode.Framework,
                    layer: 'SignalR',
                });
            }
            result && handler(result);
        };
        wrapper.handler = handler;
        return wrapper;
    }
    wrapStreamIoValuesHandler(handler) {
        const wrapper = (ev) => {
            let result;
            try {
                if (ev.eventType !== 'StreamIoValues')
                    return;
                result = ev.context;
            }
            catch (e) {
                this.logger.warn({
                    message: `Unknown message format from SignalR, skipping, ${e instanceof Error ? e.message : "Unknown error"}`,
                    mode: messaging_common_1.LogMode.Framework,
                    layer: 'SignalR',
                });
            }
            result && handler(result);
        };
        wrapper.handler = handler;
        return wrapper;
    }
}
exports.SignalrService = SignalrService;
